/**
 * PostHog Loader - RGPD Compliant
 * 
 * This module loads PostHog ONLY after explicit user consent.
 * The PostHog script is NOT loaded until consent is granted, ensuring no cookies
 * are set before user approval.
 * 
 * IMPORTANT: This function must ONLY be called after user explicitly accepts
 * analytics cookies in the cookie consent banner.
 */

// Declare global types for PostHog
// PostHog's array.js defines posthog as an array initially, then it becomes an object after init
declare global {
  interface Window {
    posthog?: {
      init: (key: string, config: Record<string, unknown>) => void;
      capture: (eventName: string, properties?: Record<string, unknown>) => void;
      identify: (distinctId: string, properties?: Record<string, unknown>) => void;
      reset: () => void;
      isFeatureEnabled: (flag: string) => boolean;
      onFeatureFlags: (callback: () => void) => void;
      getFeatureFlag: (flag: string) => boolean | string | undefined;
      reloadFeatureFlags: () => void;
      group: (type: string, key: string, properties?: Record<string, unknown>) => void;
      alias: (alias: string) => void;
      register: (properties: Record<string, unknown>) => void;
      unregister: (property: string) => void;
      get_distinct_id: () => string;
      get_session_id: () => string | undefined;
      has_opted_in_capturing: () => boolean;
      has_opted_out_capturing: () => boolean;
      opt_out_capturing: () => void;
      opt_in_capturing: () => void;
      clear_opt_in_out_capturing: () => void;
    } | Array<unknown>; // Can be an array (before init) or object (after init)
  }
}

// Get PostHog key from environment variables
const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
// IMPORTANT: Reverse proxy is DISABLED by default in production
// Set VITE_POSTHOG_USE_PROXY=false to ensure direct connection to PostHog
// This avoids 405 errors and "Unexpected token '<'" errors
const USE_REVERSE_PROXY = import.meta.env.VITE_POSTHOG_USE_PROXY === 'true';
const POSTHOG_HOST = USE_REVERSE_PROXY && typeof window !== 'undefined'
  ? window.location.origin // Utilise le reverse proxy sur le même domaine
  : import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com';

// Track if PostHog has been loaded to prevent double loading
let posthogLoaded = false;
let isLoadingPostHog = false;

/**
 * Loads PostHog dynamically after user consent
 * 
 * This function:
 * 1. Creates and appends the PostHog script tag dynamically
 * 2. Initializes PostHog with the project key
 * 3. Configures PostHog with privacy settings
 * 4. Ensures no cookies are set before consent
 * 
 * @returns Promise that resolves when PostHog is fully loaded and initialized
 */
export const loadPostHog = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      console.warn('[PostHog Loader] Window is not available');
      resolve();
      return;
    }

    // Check if PostHog key is configured
    if (!POSTHOG_KEY) {
      if (import.meta.env.DEV) {
        console.warn('[PostHog Loader] ⚠️ VITE_PUBLIC_POSTHOG_KEY not configured. PostHog will not be loaded.');
      }
      resolve();
      return;
    }

    // Prevent double loading
    if (posthogLoaded) {
      console.log('[PostHog Loader] ✅ PostHog already loaded');
      resolve();
      return;
    }

    if (isLoadingPostHog) {
      console.log('[PostHog Loader] ⏳ PostHog is already loading...');
      // Wait for existing load to complete
      const checkInterval = setInterval(() => {
        if (posthogLoaded) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
      return;
    }

    isLoadingPostHog = true;
    console.log('[PostHog Loader] 🚀 Starting PostHog load (RGPD compliant)...');

    // Create and append the script tag dynamically
    const script = document.createElement('script');
    script.async = true;
    script.src = `${POSTHOG_HOST}/static/array.js`;
    
    script.onload = () => {
      console.log('[PostHog Loader] ✅ PostHog script loaded from:', script.src);
      
      // Wait for PostHog script to be available
      // The array.js script defines window.posthog as an array initially
      const checkPostHogScript = (attempts = 0) => {
        const maxAttempts = 20; // 2 seconds max (20 * 100ms)
        
        // Check if PostHog script is loaded (it will be an array with init method)
        // After array.js loads, posthog is an array that has an init method
        const posthogArray = window.posthog as unknown as { init?: (key: string, config: Record<string, unknown>) => void } | undefined;
        const hasPostHogScript = (
          typeof window.posthog !== 'undefined' &&
          typeof posthogArray?.init === 'function'
        );

        // If script loaded but PostHog is not available after max attempts,
        // it might have returned HTML instead of JavaScript (reverse proxy error)
        if (attempts >= maxAttempts && !hasPostHogScript) {
          console.error('[PostHog Loader] ❌ PostHog script loaded but init function not available - script may have returned HTML');
          // Try direct connection as fallback
          if (USE_REVERSE_PROXY && POSTHOG_HOST === window.location.origin) {
            console.warn('[PostHog Loader] ⚠️ Script may have returned HTML from reverse proxy, trying direct connection...');
            script.remove();
            const directHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com';
            const directScript = document.createElement('script');
            directScript.async = true;
            directScript.src = `${directHost}/static/array.js`;
            directScript.onload = () => {
              // Re-run initialization with direct connection
              const retryCheck = (retryAttempts = 0) => {
                const retryMaxAttempts = 20;
                const retryPosthogArray = window.posthog as unknown as { init?: (key: string, config: Record<string, unknown>) => void } | undefined;
                const retryHasPostHogScript = (
                  typeof window.posthog !== 'undefined' &&
                  typeof retryPosthogArray?.init === 'function'
                );
                if (retryHasPostHogScript || retryAttempts >= retryMaxAttempts) {
                  if (retryPosthogArray && typeof retryPosthogArray.init === 'function') {
                    retryPosthogArray.init(POSTHOG_KEY, {
                      api_host: directHost,
                      autocapture: false,
                      capture_pageview: false,
                      capture_pageleave: false,
                      session_recording: {
                        maskAllInputs: true,
                        maskInputOptions: { password: true, email: true, tel: true, text: false, textarea: false },
                        recordCanvas: false,
                        collectFonts: false,
                        consoleLogRecordingEnabled: true,
                      },
                      person_profiles: 'identified_only',
                      capture_exceptions: true,
                      loaded: () => {
                        setTimeout(() => {
                          posthogLoaded = true;
                          isLoadingPostHog = false;
                          console.log('[PostHog Loader] ✅ PostHog initialized successfully (direct connection fallback)');
                          resolve();
                        }, 100);
                      },
                    });
                  } else {
                    isLoadingPostHog = false;
                    reject(new Error('PostHog init function not available even with direct connection'));
                  }
                } else {
                  setTimeout(() => retryCheck(retryAttempts + 1), 100);
                }
              };
              setTimeout(() => retryCheck(), 200);
            };
            directScript.onerror = () => {
              isLoadingPostHog = false;
              reject(new Error('Failed to load PostHog script from direct connection'));
            };
            document.head.appendChild(directScript);
            return;
          }
          isLoadingPostHog = false;
          reject(new Error('PostHog init function not available'));
          return;
        }

        if (hasPostHogScript || attempts >= maxAttempts) {
          try {
            // Initialize PostHog with project key
            // PostHog's array.js defines posthog as an array that becomes an object after init
            // Configuration options:
            // - api_host: PostHog host URL
            // - autocapture: Disable automatic event capture (we'll track manually)
            // - capture_pageview: Disable automatic pageview (we'll track manually for SPA)
            // - capture_pageleave: Disable automatic pageleave
            // - loaded: Callback when PostHog is ready
            if (posthogArray && typeof posthogArray.init === 'function') {
              posthogArray.init(POSTHOG_KEY, {
                api_host: POSTHOG_HOST,
                autocapture: false, // Disable automatic capture for privacy
                capture_pageview: false, // We handle pageviews manually for SPA
                capture_pageleave: false,
                // Session Recording configuration
                session_recording: {
                  // Masquer les informations sensibles (RGPD)
                  maskAllInputs: true,
                  maskInputOptions: {
                    password: true,
                    email: true,
                    tel: true,
                    // Vous pouvez démasquer certains champs non-sensibles
                    text: false,
                    textarea: false,
                  },
                  // Performance
                  recordCanvas: false, // Désactiver si pas nécessaire
                  collectFonts: false,
                  // Console logs (utile pour debugging)
                  consoleLogRecordingEnabled: true,
                },
                // Person profiles: only for identified users (réduit les coûts)
                person_profiles: 'identified_only',
                // Error tracking
                capture_exceptions: true,
                loaded: (_posthog: typeof window.posthog) => {
                  // Wait a bit more to ensure PostHog is fully ready
                  setTimeout(() => {
                    posthogLoaded = true;
                    isLoadingPostHog = false;

                    console.log('[PostHog Loader] ✅ PostHog initialized successfully');
                    console.log('[PostHog Loader] Project Key:', POSTHOG_KEY);
                    console.log('[PostHog Loader] Host:', POSTHOG_HOST);
                    console.log('[PostHog Loader] 🍪 No cookies set before consent - RGPD compliant');
                    console.log('[PostHog Loader] 🎥 Session Recording configured');
                    
                    resolve();
                  }, 100);
                },
              });
            } else {
              throw new Error('PostHog init function not available');
            }
          } catch (error) {
            isLoadingPostHog = false;
            console.error('[PostHog Loader] ❌ Error initializing PostHog:', error);
            reject(error);
          }
        } else {
          // Retry after 100ms
          setTimeout(() => checkPostHogScript(attempts + 1), 100);
        }
      };

      // Start checking after a short delay
      setTimeout(() => checkPostHogScript(), 200);
    };

    script.onerror = () => {
      isLoadingPostHog = false;
      const errorMsg = `[PostHog Loader] ❌ Failed to load PostHog script from ${script.src}`;
      console.error(errorMsg);
      
      // Always try direct connection if reverse proxy fails or if script returns HTML
      // This handles cases where reverse proxy returns HTML error pages instead of JS
      if (USE_REVERSE_PROXY && POSTHOG_HOST === window.location.origin) {
        console.warn('[PostHog Loader] ⚠️ Reverse proxy failed or returned HTML, trying direct connection...');
        const directHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';
        
        // Remove old script
        script.remove();
        
        // Create new script with direct connection
        const directScript = document.createElement('script');
        directScript.async = true;
        directScript.src = `${directHost}/static/array.js`;
        
        directScript.onload = () => {
          console.log('[PostHog Loader] ✅ PostHog script loaded from direct connection');
          // Re-run the initialization logic
          const checkPostHogScript = (attempts = 0) => {
            const maxAttempts = 20;
            const posthogArray = window.posthog as unknown as { init?: (key: string, config: Record<string, unknown>) => void } | undefined;
            const hasPostHogScript = (
              typeof window.posthog !== 'undefined' &&
              typeof posthogArray?.init === 'function'
            );

            if (hasPostHogScript || attempts >= maxAttempts) {
              try {
                if (posthogArray && typeof posthogArray.init === 'function') {
                  posthogArray.init(POSTHOG_KEY, {
                    api_host: directHost,
                    autocapture: false,
                    capture_pageview: false,
                    capture_pageleave: false,
                    session_recording: {
                      maskAllInputs: true,
                      maskInputOptions: {
                        password: true,
                        email: true,
                        tel: true,
                        text: false,
                        textarea: false,
                      },
                      recordCanvas: false,
                      collectFonts: false,
                      consoleLogRecordingEnabled: true,
                    },
                    person_profiles: 'identified_only',
                    capture_exceptions: true,
                    loaded: (_posthog: typeof window.posthog) => {
                      setTimeout(() => {
                        posthogLoaded = true;
                        isLoadingPostHog = false;
                        console.log('[PostHog Loader] ✅ PostHog initialized successfully (direct connection)');
                        resolve();
                      }, 100);
                    },
                  });
                } else {
                  throw new Error('PostHog init function not available');
                }
              } catch (error) {
                isLoadingPostHog = false;
                console.error('[PostHog Loader] ❌ Error initializing PostHog:', error);
                reject(error);
              }
            } else {
              setTimeout(() => checkPostHogScript(attempts + 1), 100);
            }
          };
          setTimeout(() => checkPostHogScript(), 200);
        };
        
        directScript.onerror = () => {
          isLoadingPostHog = false;
          console.error('[PostHog Loader] ❌ Direct connection also failed');
          reject(new Error(errorMsg));
        };
        
        // Append new script with direct connection
        document.head.appendChild(directScript);
        return;
      }
      
      reject(new Error(errorMsg));
    };

    // Append script to head
    document.head.appendChild(script);
    console.log('[PostHog Loader] 📝 Script tag appended to <head>');
    console.log('[PostHog Loader] 📍 Loading from:', script.src);
  });
};

/**
 * Check if PostHog has been loaded
 */
export const isPostHogLoaded = (): boolean => {
  if (!posthogLoaded || typeof window === 'undefined' || typeof window.posthog === 'undefined') {
    return false;
  }
  // Check if posthog is an object (not an array) and has capture method
  const posthog = window.posthog;
  if (Array.isArray(posthog)) {
    return false;
  }
  return typeof (posthog as { capture?: unknown }).capture === 'function';
};

/**
 * Diagnostic helper function (available in production for debugging)
 * Exposes window.diagnosePostHog() for debugging
 */
if (typeof window !== 'undefined') {
  (window as Window & { diagnosePostHog?: () => unknown }).diagnosePostHog = () => {
    const scriptTag = document.querySelector('script[src*="posthog.com/static/array.js"]');
    const cookieConsent = localStorage.getItem('rakb_cookie_consent');
    const cookiePrefs = localStorage.getItem('rakb_cookie_preferences');
    let parsedPrefs = null;
    try {
      parsedPrefs = cookiePrefs ? JSON.parse(cookiePrefs) : null;
    } catch {
      // Ignore parsing errors
    }

    const posthog = window.posthog;
    const isPostHogObject = posthog && !Array.isArray(posthog);
    const hasCapture = isPostHogObject && typeof (posthog as { capture?: unknown }).capture === 'function';
    const distinctId = isPostHogObject && typeof (posthog as { get_distinct_id?: () => string }).get_distinct_id === 'function'
      ? (posthog as { get_distinct_id: () => string }).get_distinct_id()
      : 'N/A';

    const diagnostics = {
      'PostHog Loaded (internal)': posthogLoaded,
      'posthog exists': typeof window.posthog !== 'undefined',
      'posthog.capture exists': hasCapture,
      'Project Key': POSTHOG_KEY || 'NOT CONFIGURED',
      'Host': POSTHOG_HOST,
      'Script in DOM': !!scriptTag,
      'Script src': scriptTag?.getAttribute('src') || 'NOT FOUND',
      'Cookie consent': cookieConsent || 'NOT SET',
      'Analytics enabled': parsedPrefs?.analytics || false,
      'Can capture events': hasCapture,
      'Distinct ID': distinctId,
    };

    console.log('=== POSTHOG DIAGNOSTIC ===');
    console.table(diagnostics);
    console.log('posthog object:', window.posthog);
    console.log('Cookie preferences:', parsedPrefs);
    console.log('===================================');

    return diagnostics;
  };

  // Also expose a test function to manually send events
  (window as Window & { testPostHogEvent?: () => void }).testPostHogEvent = () => {
    const posthog = window.posthog;
    if (posthog && !Array.isArray(posthog) && typeof (posthog as { capture?: unknown }).capture === 'function') {
      (posthog as { capture: (eventName: string, properties?: Record<string, unknown>) => void }).capture('test_event', {
        event_category: 'debug',
        event_label: 'manual_test',
        value: 1,
      });
      console.log('✅ Test event sent to PostHog');
      console.log('Check PostHog dashboard for the event');
    } else {
      console.error('❌ PostHog capture function not available');
    }
  };

  if (import.meta.env.DEV) {
    console.log('[PostHog Loader] Diagnostic helpers available:');
    console.log('  - window.diagnosePostHog() - Full diagnostic');
    console.log('  - window.testPostHogEvent() - Send test event');
  }
}

